import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceService } from '../services/api';
import Navbar from '../components/Navbar';
import './StudentDashboard.css';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [todayStatus, setTodayStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [attendanceData, setAttendanceData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAbsentModal, setShowAbsentModal] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [absentReason, setAbsentReason] = useState('');

  useEffect(() => {
    checkTodayStatus();
    fetchAttendance();
  }, [selectedMonth, selectedYear]);

  const checkTodayStatus = async () => {
    try {
      const data = await attendanceService.checkTodayStatus();
      setTodayStatus(data);
    } catch (error) {
      console.error('Error checking today status:', error);
    }
  };

  const fetchAttendance = async () => {
    try {
      const data = await attendanceService.getMyAttendance(selectedMonth, selectedYear);
      setAttendanceData(data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const handleMarkPresent = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await attendanceService.markPresent();
      setMessage({ type: 'success', text: result.message || 'Attendance marked successfully!' });
      await checkTodayStatus();
      await fetchAttendance();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to mark attendance' });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAbsentToday = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await attendanceService.markAbsent(new Date().toISOString().split('T')[0], absentReason);
      setMessage({ type: 'success', text: 'Marked as absent for today' });
      await checkTodayStatus();
      await fetchAttendance();
      setAbsentReason('');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to mark absent' });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkMultipleAbsent = async () => {
    if (selectedDates.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one date' });
      return;
    }

    setLoading(true);
    try {
      const result = await attendanceService.markAbsentMultiple(selectedDates, absentReason);
      setMessage({ type: 'success', text: result.message });
      setShowAbsentModal(false);
      setSelectedDates([]);
      setAbsentReason('');
      await fetchAttendance();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to mark absences' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getAttendancePercentage = () => {
    if (!attendanceData || !attendanceData.workingDays) return 0;
    return ((attendanceData.totalPresent / attendanceData.workingDays) * 100).toFixed(1);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'present': return 'status-badge present';
      case 'absent': return 'status-badge absent';
      case 'not_marked': return 'status-badge not-marked';
      default: return 'status-badge';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'present': return 'Present';
      case 'absent': return 'Absent';
      case 'not_marked': return 'Not Marked';
      default: return status;
    }
  };

  const downloadCSV = () => {
    if (!attendanceData || !attendanceData.attendance.length) {
      setMessage({ type: 'error', text: 'No data to download' });
      return;
    }

    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });
    const headers = ['Date', 'Day', 'Status', 'Check-in Time'];
    const rows = attendanceData.attendance.map(record => [
      new Date(record.date).toLocaleDateString(),
      new Date(record.date).toLocaleDateString('en-US', { weekday: 'long' }),
      getStatusText(record.status),
      record.checkInTime ? formatTime(record.checkInTime) : '-'
    ]);

    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Working Days', attendanceData.workingDays]);
    rows.push(['Days Present', attendanceData.totalPresent]);
    rows.push(['Days Absent', attendanceData.totalAbsent]);
    rows.push(['Not Marked', attendanceData.notMarked || 0]);
    rows.push(['Attendance Rate', `${getAttendancePercentage()}%`]);

    const csvContent = [
      [`Attendance Report - ${user?.name}`],
      [`Lab ID: ${user?.labId}`],
      [`Period: ${monthName} ${selectedYear}`],
      [],
      headers,
      ...rows
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${user?.name?.replace(/\s+/g, '_')}_${monthName}_${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    if (!attendanceData || !attendanceData.attendance.length) {
      setMessage({ type: 'error', text: 'No data to download' });
      return;
    }

    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Attendance Report - ${user?.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1a1a2e; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 2px solid #e0e0e0; }
          .header h1 { font-size: 24px; font-weight: 600; color: #1a1a2e; margin-bottom: 8px; }
          .header p { color: #666; font-size: 14px; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 30px; background: #f8f9fa; padding: 20px; border-radius: 8px; }
          .info-item { font-size: 14px; }
          .info-item strong { color: #1a1a2e; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #1a1a2e; color: white; padding: 12px; text-align: left; font-size: 13px; font-weight: 500; }
          td { padding: 10px 12px; border-bottom: 1px solid #e0e0e0; font-size: 13px; }
          tr:nth-child(even) { background: #f8f9fa; }
          .status-present { color: #059669; font-weight: 500; }
          .status-absent { color: #dc2626; font-weight: 500; }
          .status-not-marked { color: #d97706; font-weight: 500; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
          .summary-item { text-align: center; padding: 20px; background: #f8f9fa; border-radius: 8px; }
          .summary-item .value { font-size: 28px; font-weight: 600; color: #1a1a2e; }
          .summary-item .label { font-size: 12px; color: #666; margin-top: 4px; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 11px; padding-top: 20px; border-top: 1px solid #e0e0e0; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>RNA Biology Lab</h1>
          <p>Attendance Report</p>
        </div>
        <div class="info-grid">
          <div class="info-item"><strong>Name:</strong> ${user?.name}</div>
          <div class="info-item"><strong>Lab ID:</strong> ${user?.labId}</div>
          <div class="info-item"><strong>Period:</strong> ${monthName} ${selectedYear}</div>
          <div class="info-item"><strong>Generated:</strong> ${new Date().toLocaleDateString()}</div>
        </div>
        <table>
          <thead>
            <tr><th>Date</th><th>Day</th><th>Status</th><th>Check-in Time</th></tr>
          </thead>
          <tbody>
            ${attendanceData.attendance.map(record => `
              <tr>
                <td>${new Date(record.date).toLocaleDateString()}</td>
                <td>${new Date(record.date).toLocaleDateString('en-US', { weekday: 'long' })}</td>
                <td class="status-${record.status === 'not_marked' ? 'not-marked' : record.status}">${getStatusText(record.status)}</td>
                <td>${record.checkInTime ? formatTime(record.checkInTime) : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="summary">
          <div class="summary-item"><div class="value">${attendanceData.workingDays}</div><div class="label">Working Days</div></div>
          <div class="summary-item"><div class="value">${attendanceData.totalPresent}</div><div class="label">Present</div></div>
          <div class="summary-item"><div class="value">${attendanceData.totalAbsent}</div><div class="label">Absent</div></div>
          <div class="summary-item"><div class="value">${getAttendancePercentage()}%</div><div class="label">Attendance</div></div>
        </div>
        <div class="footer">RNA Biology Lab Attendance System</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 200);
  };

  const getPastWorkingDays = () => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push(date.toISOString().split('T')[0]);
      }
    }
    return days;
  };

  return (
    <div className="dashboard-page">
      <Navbar />
      <div className="dashboard-wrapper">
        <main className="dashboard-main">
          {/* Today's Status Card */}
          <section className="status-card">
            <div className="status-header">
              <h2>Today's Attendance</h2>
              <span className="date-display">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>

            {todayStatus?.isWeekend ? (
              <div className="weekend-notice">
                <span className="weekend-icon">Weekend</span>
                <p>No attendance tracking on weekends</p>
              </div>
            ) : (
              <div className="status-content">
                {!todayStatus?.hasMarked ? (
                  <>
                    <div className="status-indicator not-marked">
                      <span className="status-icon">!</span>
                      <span className="status-text">Not Marked</span>
                    </div>
                    <p className="status-note">
                      Please mark your attendance for today.
                    </p>
                    <div className="status-actions">
                      <button
                        onClick={handleMarkPresent}
                        className="btn btn-primary btn-large"
                        disabled={loading}
                      >
                        {loading ? 'Marking...' : 'Mark Present'}
                      </button>
                      <button
                        onClick={handleMarkAbsentToday}
                        className="btn btn-secondary"
                        disabled={loading}
                      >
                        Mark Absent
                      </button>
                    </div>
                  </>
                ) : todayStatus?.isPresent ? (
                  <>
                    <div className="status-indicator present">
                      <span className="status-icon">✓</span>
                      <span className="status-text">Present</span>
                    </div>
                    <p className="status-note">
                      You marked present at <strong>{formatTime(todayStatus.checkInTime)}</strong>
                    </p>
                  </>
                ) : (
                  <>
                    <div className="status-indicator absent">
                      <span className="status-icon">✗</span>
                      <span className="status-text">Absent</span>
                    </div>
                    <p className="status-note">
                      You have marked yourself as absent today.
                    </p>
                    <div className="status-actions">
                      <button
                        onClick={handleMarkPresent}
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? 'Updating...' : 'Change to Present'}
                      </button>
                    </div>
                  </>
                )}

                {todayStatus?.hasMarked && (
                  <div className="additional-actions">
                    <button
                      onClick={() => setShowAbsentModal(true)}
                      className="btn btn-outline"
                    >
                      Mark Past Days Absent
                    </button>
                  </div>
                )}
              </div>
            )}

            {message.text && (
              <div className={`message-toast ${message.type}`}>
                {message.text}
              </div>
            )}
          </section>

          {/* Attendance Record Card */}
          <section className="record-card">
            <div className="record-header">
              <h2>Attendance Record</h2>
              <div className="record-controls">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="select-input"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="select-input"
                >
                  {Array.from({ length: 3 }, (_, i) => (
                    <option key={i} value={new Date().getFullYear() - 1 + i}>
                      {new Date().getFullYear() - 1 + i}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {attendanceData && (
              <>
                <div className="stats-grid">
                  <div className="stat-box">
                    <span className="stat-value">{attendanceData.workingDays}</span>
                    <span className="stat-label">Working Days</span>
                  </div>
                  <div className="stat-box present">
                    <span className="stat-value">{attendanceData.totalPresent}</span>
                    <span className="stat-label">Present</span>
                  </div>
                  <div className="stat-box absent">
                    <span className="stat-value">{attendanceData.totalAbsent}</span>
                    <span className="stat-label">Absent</span>
                  </div>
                  <div className="stat-box not-marked">
                    <span className="stat-value">{attendanceData.notMarked || 0}</span>
                    <span className="stat-label">Not Marked</span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-value">{getAttendancePercentage()}%</span>
                    <span className="stat-label">Attendance</span>
                  </div>
                </div>

                <div className="export-buttons">
                  <button onClick={downloadCSV} className="btn btn-sm">Export CSV</button>
                  <button onClick={downloadPDF} className="btn btn-sm">Export PDF</button>
                </div>

                {attendanceData.attendance.length > 0 ? (
                  <div className="attendance-table-wrapper">
                    <table className="attendance-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Day</th>
                          <th>Status</th>
                          <th>Check-in Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceData.attendance.map((record, index) => (
                          <tr key={index}>
                            <td>{new Date(record.date).toLocaleDateString()}</td>
                            <td>{new Date(record.date).toLocaleDateString('en-US', { weekday: 'short' })}</td>
                            <td>
                              <span className={getStatusBadgeClass(record.status)}>
                                {getStatusText(record.status)}
                              </span>
                            </td>
                            <td className="time-cell">
                              {record.checkInTime ? formatTime(record.checkInTime) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No attendance records for this period</p>
                  </div>
                )}
              </>
            )}
          </section>
        </main>

        {/* Mark Absent Modal */}
        {showAbsentModal && (
          <div className="modal-overlay" onClick={() => setShowAbsentModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Mark Past Days as Absent</h3>
                <button className="modal-close" onClick={() => setShowAbsentModal(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <p className="modal-note">Select the dates you were absent (last 14 working days)</p>
                <div className="date-checkboxes">
                  {getPastWorkingDays().map(date => (
                    <label key={date} className="date-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedDates.includes(date)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDates([...selectedDates, date]);
                          } else {
                            setSelectedDates(selectedDates.filter(d => d !== date));
                          }
                        }}
                      />
                      <span>{formatDate(date)}</span>
                    </label>
                  ))}
                </div>
                <div className="form-group">
                  <label>Reason (optional)</label>
                  <input
                    type="text"
                    value={absentReason}
                    onChange={(e) => setAbsentReason(e.target.value)}
                    placeholder="e.g., Medical leave, Conference"
                    className="text-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setShowAbsentModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleMarkMultipleAbsent}
                  disabled={loading || selectedDates.length === 0}
                >
                  {loading ? 'Saving...' : `Mark ${selectedDates.length} Day(s) Absent`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
